import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/empresa', label: 'Empresa' },
    { path: '/servicios', label: 'Servicios' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contacto', label: 'Contacto' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/3f5d024e-3e6d-4d97-ae36-0220e5cf8b45.png" 
              alt="PLADUREX Logo" 
              className="h-8 sm:h-10 lg:h-12 w-auto flex-shrink-0"
            />
            <span className="ml-2 sm:ml-3 text-lg sm:text-xl lg:text-2xl font-bold text-text-dark truncate">PLADUREX</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm lg:text-base font-medium transition-colors relative group whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : 'text-text-dark hover:text-primary'
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary transform origin-left transition-transform duration-300 ${
                    location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
            <a
              href="tel:+34629501941"
              className="px-3 lg:px-5 py-2 lg:py-2.5 border-2 border-primary text-primary font-semibold text-sm lg:text-base rounded-lg hover:bg-primary hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-1 lg:mr-2"></i>
              Llamar
            </a>
            <a
              href="https://wa.me/34629501941"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 lg:px-5 py-2 lg:py-2.5 bg-primary text-white font-semibold text-sm lg:text-base rounded-lg hover:bg-primary-dark transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-1 lg:mr-2"></i>
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-bg-light transition-colors cursor-pointer flex-shrink-0"
            aria-label="Toggle menu"
          >
            <i className={`text-xl sm:text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-white border-t border-gray-200 overflow-hidden"
        >
          <nav className="px-3 sm:px-4 py-4 space-y-2 sm:space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2.5 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                  location.pathname === link.path
                    ? 'bg-primary text-white'
                    : 'text-text-dark hover:bg-bg-light'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 sm:pt-3 space-y-2">
              <a
                href="tel:+34629501941"
                className="block w-full px-4 py-2.5 sm:py-3 border-2 border-primary text-primary font-semibold text-sm sm:text-base rounded-lg text-center hover:bg-primary hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Llamar
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full px-4 py-2.5 sm:py-3 bg-primary text-white font-semibold text-sm sm:text-base rounded-lg text-center hover:bg-primary-dark transition-all cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp
              </a>
            </div>
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
